import { useState } from "react";
import { Modal } from "./ui";
import { Icon } from "./Icons";
import { STUDIO, actions } from "../lib/store";
import { notify, type NotifyResult } from "../lib/notify";
import { clientInviteText, clientInviteEmail, openWhatsApp, waPhone } from "../lib/whatsapp";
import { clientFeatureSentence } from "../lib/portalFeatures";
import type { Project } from "../lib/types";

/* Invite the client into their portal — the link plus their personal access
   code, by WhatsApp, by branded email, or copied by hand. */

export function InviteClient({ p, open, onClose }: { p: Project; open: boolean; onClose: () => void }) {
  const url = `${location.origin}/portal/${p.id}`;
  const can = clientFeatureSentence(p);
  const parts = { name: p.client.name, studio: STUDIO.name, project: p.name, url, code: p.client.code, can };
  const [copied, setCopied] = useState<"link" | "code" | null>(null);
  const [phone, setPhone] = useState(p.client.phone ?? "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<NotifyResult | null>(null);

  function copy(what: "link" | "code") {
    navigator.clipboard?.writeText(what === "link" ? url : p.client.code);
    setCopied(what); setTimeout(() => setCopied(null), 1800);
  }

  function whatsapp() {
    const clean = phone.trim();
    // remember the number so the next invite is one tap
    if (clean && clean !== (p.client.phone ?? "")) actions.updateClient(p.id, { phone: clean });
    openWhatsApp(clientInviteText(parts), clean || undefined);
  }

  async function email() {
    if (!p.client.email) return;
    setSending(true); setSent(null);
    const r = await notify({
      to: p.client.email,
      subject: `Your project portal — ${p.name}`,
      heading: `Welcome to ${p.name}`,
      body: clientInviteEmail(parts),
      cta: { label: "Open your portal", url },
    });
    setSent(r); setSending(false);
  }

  const sentNote = sent === "delivered" ? `Sent to ${p.client.email}.`
    : sent === "simulated" ? "Email isn't switched on for the studio yet, so nothing was actually sent — use WhatsApp or copy the link."
    : sent === "failed" ? "That didn't send. Try WhatsApp or copy the link instead."
    : null;

  return (
    <Modal open={open} onClose={onClose} title={`Invite ${p.client.name}`} wide>
      <p className="muted" style={{ fontSize: 14.5, lineHeight: 1.6, marginBottom: 18 }}>
        Give {p.client.name.split(" ")[0]} access to the portal for <b>{p.name}</b> — their one place to {can}. The invitation only mentions what you've turned on for them; the code is personal to them.
      </p>

      {/* link + code */}
      <div className="panel" style={{ padding: 14, marginBottom: 16 }}>
        <div className="row between wrap" style={{ gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div className="kicker" style={{ fontSize: 9 }}>Portal link</div>
            <div className="mono" style={{ fontSize: 12.5, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis" }}>{url}</div>
          </div>
          <button className="chip" onClick={() => copy("link")}>{copied === "link" ? <><Icon.check width={12} />Copied</> : <><Icon.link width={12} />Copy link</>}</button>
        </div>
        <div className="divider" style={{ margin: "12px 0" }} />
        <div className="row between wrap" style={{ gap: 10 }}>
          <div>
            <div className="kicker" style={{ fontSize: 9 }}>Access code</div>
            <div className="mono" style={{ fontSize: 15, marginTop: 3, letterSpacing: ".06em" }}>{p.client.code}</div>
          </div>
          <button className="chip" onClick={() => copy("code")}>{copied === "code" ? <><Icon.check width={12} />Copied</> : <><Icon.brief width={12} />Copy code</>}</button>
        </div>
      </div>

      {/* whatsapp */}
      <div className="field" style={{ marginBottom: 8 }}>
        <label>WhatsApp number <span className="dim">— optional, leave blank to pick the contact yourself</span></label>
        <div className="row" style={{ gap: 8 }}>
          <input className="input mono" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+592 600 0000" />
          <button className="btn" style={{ background: "#25D366", borderColor: "#25D366", color: "#fff", whiteSpace: "nowrap" }} onClick={whatsapp}>
            <Icon.whatsapp width={16} />Invite on WhatsApp
          </button>
        </div>
        {phone.trim() && waPhone(phone).length < 8 && <div style={{ color: "var(--warn)", fontSize: 11.5, marginTop: 5 }}>That looks short for a full number — include the country code.</div>}
      </div>

      {/* email */}
      <div className="row between wrap" style={{ gap: 10, marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--line)" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>Email the invitation</div>
          <div className="dim" style={{ fontSize: 12 }}>{p.client.email || "No email on file for this client."}</div>
        </div>
        <button className="btn ghost sm" onClick={email} disabled={!p.client.email || sending}>
          <Icon.send width={14} />{sending ? "Sending…" : "Send email"}
        </button>
      </div>
      {sentNote && (
        <div className="dim" style={{ fontSize: 12.5, marginTop: 10 }}>
          {sent === "delivered" ? <span className="badge ok"><Icon.check width={12} />{sentNote}</span> : sentNote}
        </div>
      )}
    </Modal>
  );
}
